// Booking a machine in for maintenance.
//
// Its own modal rather than a mode on BookingModal, and that is a deliberate
// call: the two share a shape (a resource, a date range, a conflict panel) and
// nothing else. A maintenance booking has no job, no times and no worker to
// notify, so a combined form would be three conditionals deep in the fields
// people use most — and the reason the server refuses to change a booking's
// species is the same reason the form does not offer to.
//
// No time fields at all. Maintenance is all-day by construction: the server
// takes no times and a database CHECK agrees (migration 013). Offering a field
// the server will ignore is a form that lies.
//
// Editing lives here too (found 2026-08-26: the schedule used to open every
// booking in BookingModal, so a maintenance bar got a job form with an empty
// Job select). Same shape as BookingModal's edit mode: `booking` present means
// editing, and Release appears — but still no job and no times, because an
// edit dialog must not offer what the booking cannot hold.
import { useEffect, useMemo, useState } from 'react';
import {
  conflictsFrom,
  scheduling,
  type Booking,
  type CalendarBooking,
  type Resource,
} from '../schedulingApi';
import { ApiError } from '../api';
import { MAINTAINABLE_KINDS } from './schedule/views';
import { Modal, SearchableSelect } from './ui';
import { nwLabel, type NwLookup } from '../lib/nonWorkingDays';
import {
  ScheduleConflictPanel,
  isReleasable,
  type ResourceConflicts,
} from './schedule/ScheduleConflictPanel';

export function MaintenanceModal({
  resources,
  booking,
  initial,
  onClose,
  onSaved,
  onReleased,
  nwInfo,
}: {
  resources: Resource[];
  /** Present when editing; absent when booking new. */
  booking?: CalendarBooking | null;
  initial?: { startDate?: string; endDate?: string; resourceId?: string };
  onClose: () => void;
  onSaved: (booking: Booking) => void;
  onReleased?: (bookingId: string) => void;
  nwInfo?: NwLookup;
}) {
  const editing = Boolean(booking);

  const [resourceId, setResourceId] = useState(booking?.resourceId ?? initial?.resourceId ?? '');
  const [supplierId, setSupplierId] = useState(booking?.supplierId ?? '');
  const [startDate, setStartDate] = useState(booking?.startDate ?? initial?.startDate ?? '');
  const [endDate, setEndDate] = useState(
    booking?.endDate ?? initial?.endDate ?? initial?.startDate ?? ''
  );
  const [note, setNote] = useState(booking?.note ?? '');
  const [panel, setPanel] = useState<ResourceConflicts | null>(null);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Only machines can go to a mechanic; a person going away is leave, which is
  // the availability panel's job. The server says the same thing. On an edit
  // the booking's own machine stays in the list even if archived since, so the
  // form is not silently rewritten — BookingModal's rule.
  const machines = useMemo(
    () =>
      resources
        .filter(
          (r) =>
            (MAINTAINABLE_KINDS.includes(r.kind) && r.status !== 'archived') ||
            r.id === booking?.resourceId
        )
        .sort((a, b) => a.label.localeCompare(b.label)),
    [resources, booking]
  );

  const suppliers = useMemo(
    () =>
      resources
        .filter(
          (r) => (r.kind === 'supplier' && r.status !== 'archived') || r.id === booking?.supplierId
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [resources, booking]
  );

  // Keep the end from ever sitting before the start, which is the one mistake
  // this form makes easy.
  useEffect(() => {
    if (startDate && endDate && endDate < startDate) setEndDate(startDate);
  }, [startDate, endDate]);

  function body(extra: { dryRun?: boolean; release?: string[]; allowPastRewrite?: boolean } = {}) {
    return {
      resourceId,
      supplierId,
      startDate,
      endDate,
      note: note.trim() === '' ? null : note,
      ...extra,
    };
  }

  async function save(extra: { release?: string[]; allowPastRewrite?: boolean } = {}) {
    const result = editing
      ? await scheduling.updateBooking(booking!.id, body(extra))
      : await scheduling.createMaintenance(body(extra));
    onSaved(result.booking);
  }

  /** ZT's two-phase save, single-machine edition: dry-run, panel, apply. */
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setPanel(null);
    try {
      try {
        if (editing) await scheduling.updateBooking(booking!.id, body({ dryRun: true }));
        else await scheduling.createMaintenance(body({ dryRun: true }));
      } catch (err) {
        const found = conflictsFrom(err);
        if (found) {
          const machine = machines.find((m) => m.id === resourceId);
          setPanel({
            resourceId,
            resourceName: machine?.label ?? 'That machine',
            conflicts: found,
          });
          setTicked(new Set([resourceId]));
          return;
        }
        throw err;
      }
      await save();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not book that in.');
    } finally {
      setBusy(false);
    }
  }

  async function confirmFromPanel() {
    if (!panel) return;
    setBusy(true);
    setError(null);
    try {
      if (!ticked.has(resourceId) || !isReleasable(panel)) {
        // One machine, unticked or unreleasable: there is nothing to schedule.
        setError('That machine stays where it is; change the dates instead.');
        return;
      }
      const clashes = panel.conflicts.filter((c) => c.type === 'booking');
      await save({
        release: clashes.map((c) => c.bookingId),
        allowPastRewrite: clashes.some(
          (c) => c.ifReleased.action !== 'none' && c.ifReleased.rewritesPast
        ),
      });
    } catch (err) {
      const fresh = conflictsFrom(err);
      if (fresh) {
        setError('Something changed while you were looking. Check again.');
        setPanel(null);
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not book that in.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function release() {
    if (!booking) return;
    setBusy(true);
    setError(null);
    try {
      const result = await scheduling.releaseBooking(booking.id);
      onReleased?.(booking.id);
      if (result.outcome === 'truncated') {
        // Same rule as a job booking: the days it has already been away stay
        // on the record, and that is worth saying out loud.
        window.alert(
          `That maintenance had already started, so the days the machine has been away stay ` +
            `on the record. It now ends on ${result.newEndDate}.`
        );
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not release that booking.');
    } finally {
      setBusy(false);
    }
  }

  const ready = resourceId !== '' && supplierId !== '' && startDate !== '' && endDate !== '';

  return (
    <Modal
      title={editing ? 'Edit maintenance' : 'Book in for maintenance'}
      onClose={onClose}
      footer={
        <>
          {editing && (
            <button
              type="button"
              className="row-action-danger mr-auto"
              onClick={() => void release()}
              disabled={busy}
            >
              Release
            </button>
          )}
          <button type="button" onClick={onClose} className="btn-ghost btn-sm">
            Cancel
          </button>
          <button
            type="submit"
            form="maintenance"
            disabled={busy || !ready}
            className="btn-primary btn-sm"
          >
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Book it in'}
          </button>
        </>
      }
    >
      {error && (
        <div className="banner-error mb-3" role="alert">
          {error}
        </div>
      )}

      {panel && (
        <ScheduleConflictPanel
          entries={[panel]}
          ticked={ticked}
          onToggle={(rid) =>
            setTicked((current) => {
              const next = new Set(current);
              if (next.has(rid)) next.delete(rid);
              else next.add(rid);
              return next;
            })
          }
          confirmLabel={ticked.has(resourceId) ? 'Move & schedule' : 'Nothing to schedule'}
          onConfirm={() => void confirmFromPanel()}
          busy={busy}
        />
      )}

      <form id="maintenance" onSubmit={submit} noValidate className="space-y-4">
        {/* "Machine" rather than "Resource", which is the word ZT uses on this
            column and the word a person standing in a yard uses. */}
        <div>
          <label className="form-label" htmlFor="maintenance-resource">
            Machine
          </label>
          <SearchableSelect
            id="maintenance-resource"
            value={resourceId}
            onChange={setResourceId}
            placeholder="Which machine is going in?"
            options={machines.map((r) => ({ value: r.id, label: r.label }))}
          />
        </div>

        <div>
          <label className="form-label" htmlFor="maintenance-supplier">
            Supplier
          </label>
          <SearchableSelect
            id="maintenance-supplier"
            value={supplierId}
            onChange={setSupplierId}
            placeholder="Who is doing the work?"
            options={suppliers.map((r) => ({ value: r.id, label: r.name }))}
          />
          {suppliers.length === 0 && (
            <p className="form-hint">
              No suppliers yet. Add the mechanic or workshop under Resources → Suppliers first;
              they are recorded so you know who has the machine, and are never booked onto jobs.
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="form-label" htmlFor="maintenance-start">
              From
            </label>
            <input
              id="maintenance-start"
              type="date"
              className="form-input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="form-label" htmlFor="maintenance-end">
              To
            </label>
            <input
              id="maintenance-end"
              type="date"
              className="form-input"
              min={startDate || undefined}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <p className="form-hint">
              Inclusive, and all day; a machine at the mechanic is not available for the morning.
            </p>
          </div>
        </div>

        {startDate !== '' && nwInfo?.(startDate) && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-red-700">
            Heads up: the start date is a non-working day: {nwLabel(nwInfo(startDate)!)}. You can
            still schedule it.
          </p>
        )}

        <div>
          <label className="form-label" htmlFor="maintenance-note">
            Note
          </label>
          <input
            id="maintenance-note"
            className="form-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="250-hour service, track tension"
          />
        </div>
      </form>
    </Modal>
  );
}
